import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  GlassTable,
  SkeletonList,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { resolve_label } from '../../lib/data_label';
import { AllowanceDrawer } from './allowance_drawer';
import { DeductionDrawer } from './deduction_drawer';
import {
  allowance_rule_label,
  deduction_rule_label,
  resolve_data_centre_view,
} from './data_centre_utils';
import type { RuleLabel } from './data_centre_utils';
import { useDataCentre } from '../../data/use_data_centre';
import { DataCentreBankSection } from './data_centre_bank_section';
import { DataCentreLoanSection } from './data_centre_loan_section';
import {
  format_basis_points,
  format_money,
  format_multiplier,
} from '../../lib/format';
import { DATA_CENTRE_TABS, navigate } from '../../lib/router';
import type { DataCentreTab } from '../../lib/router';
import type { Allowance, Deduction } from '../../data/types';

const TAB_LABEL_KEY: Record<DataCentreTab, string> = {
  allowances: 'nav.dc_allowances',
  deductions: 'nav.dc_deductions',
  tax: 'nav.dc_tax',
  bank: 'nav.dc_bank',
  loans: 'nav.dc_loans',
};

/** Reference data behind every pay run: allowances, deductions, tax, bank, loans. */
export function DataCentreScreen({
  tab,
}: {
  tab: DataCentreTab;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    state,
    allowances,
    deductions,
    payroll_settings,
    payment_source_accounts,
    save_allowance,
    save_deduction,
    archive_pay_item,
    retry,
  } = useDataCentre();
  const [editing, set_editing] = useState<Allowance | null>(null);
  const [drawer_open, set_drawer_open] = useState(false);
  const [editing_deduction, set_editing_deduction] = useState<Deduction | null>(
    null,
  );
  const [deduction_drawer_open, set_deduction_drawer_open] = useState(false);
  const [archiving_id, set_archiving_id] = useState<string | null>(null);
  const [archive_loading, set_archive_loading] = useState(false);
  const [archive_error, set_archive_error] = useState(false);
  const view = resolve_data_centre_view(state);
  // `save_allowance`/`save_deduction` are `undefined` whenever the write path
  // isn't reachable (read not `ready` yet, or a read-only OAuth grant) -- hide
  // the add/edit affordances entirely rather than offering a button that
  // would 403, mirroring `use_pay_runs.ts`'s `start_pay_run`/`sign_off` gate.
  const can_write_allowances = save_allowance !== undefined;
  const can_write_deductions = save_deduction !== undefined;
  const can_archive = archive_pay_item !== undefined;

  const rule_text = (label: RuleLabel): string =>
    t(label.key, {
      amount:
        label.amount === undefined
          ? ''
          : format_money(label.amount, 'LKR', locale),
      rate:
        label.rate === undefined ? '' : format_basis_points(label.rate, locale),
      multiple:
        label.multiple_bp === undefined
          ? ''
          : format_multiplier(label.multiple_bp, locale),
    });

  return (
    <>
      <ScreenHeader
        title={t('data_centre.title')}
        description={t('data_centre.subtitle')}
        actions={
          tab === 'allowances' && can_write_allowances ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                set_editing(null);
                set_drawer_open(true);
              }}
            >
              {t('data_centre.add_allowance')}
            </Button>
          ) : tab === 'deductions' && can_write_deductions ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                set_editing_deduction(null);
                set_deduction_drawer_open(true);
              }}
            >
              {t('data_centre.add_deduction')}
            </Button>
          ) : undefined
        }
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          navigate({
            module: 'payroll',
            tab: 'data-centre',
            data_centre_tab: next as DataCentreTab,
          });
        }}
      >
        <TabsList className="wp-tabstrip mb-3.5">
          {DATA_CENTRE_TABS.map((id) => (
            <TabsTrigger key={id} value={id}>
              {t(TAB_LABEL_KEY[id])}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {(tab === 'allowances' || tab === 'deductions') && view === 'loading' && (
        <GlassTable>
          <div className="p-4">
            <span className="sr-only">{t('data_centre.loading')}</span>
            <SkeletonList rows={5} />
          </div>
        </GlassTable>
      )}

      {(tab === 'allowances' || tab === 'deductions') &&
        view === 'no_entity' && (
          <EmptyState
            title={t('data_centre.no_entity_title')}
            description={t('data_centre.no_entity_description')}
          />
        )}

      {(tab === 'allowances' || tab === 'deductions') &&
        view === 'entitlement_required' && (
          <EmptyState
            title={t('data_centre.entitlement_required_title')}
            description={t('data_centre.entitlement_required_description')}
          />
        )}

      {(tab === 'allowances' || tab === 'deductions') && view === 'error' && (
        <EmptyState
          title={t('data_centre.error_title')}
          description={t('data_centre.error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('data_centre.retry')}
            </Button>
          }
        />
      )}

      {tab === 'allowances' && view === 'ready' && (
        <GlassTable>
          <table className="nv-table">
            <thead>
              <tr>
                <th scope="col">{t('data_centre.column_allowance')}</th>
                <th scope="col">{t('data_centre.column_rule')}</th>
                <th scope="col">{t('data_centre.column_taxable')}</th>
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {allowances.map((allowance) => (
                <tr key={allowance.id}>
                  <td className="font-semibold">
                    {resolve_label(t, allowance.name, allowance.name_key)}
                  </td>
                  <td>{rule_text(allowance_rule_label(allowance.rule))}</td>
                  <td>
                    <Badge
                      variant={allowance.taxable ? 'warning' : 'neutral'}
                      className="badge-no-dot"
                    >
                      {allowance.taxable
                        ? t('data_centre.taxable')
                        : t('data_centre.non_taxable')}
                    </Badge>
                  </td>
                  <td className="text-end">
                    {(can_write_allowances || can_archive) && (
                      <div className="flex justify-end gap-1">
                        {can_write_allowances && (
                          <Button
                            variant="secondary"
                            size="xs"
                            onClick={() => {
                              set_editing(allowance);
                              set_drawer_open(true);
                            }}
                          >
                            {t('common.edit')}
                          </Button>
                        )}
                        {can_archive && (
                          <Button
                            variant="ghost-danger"
                            size="xs"
                            onClick={() => {
                              set_archiving_id(allowance.id);
                            }}
                          >
                            {t('data_centre.archive')}
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassTable>
      )}

      {tab === 'deductions' && view === 'ready' && (
        <GlassTable>
          <table className="nv-table">
            <thead>
              <tr>
                <th scope="col">{t('data_centre.column_deduction')}</th>
                <th scope="col">{t('data_centre.column_rule')}</th>
                <th scope="col">{t('data_centre.column_type')}</th>
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {deductions.map((deduction) => (
                <tr key={deduction.id}>
                  <td className="font-semibold">
                    {resolve_label(t, deduction.name, deduction.name_key)}
                  </td>
                  <td>{rule_text(deduction_rule_label(deduction.rule))}</td>
                  <td>
                    <Badge
                      variant={deduction.statutory ? 'info' : 'neutral'}
                      className="badge-no-dot"
                    >
                      {deduction.statutory
                        ? t('data_centre.statutory')
                        : t('data_centre.voluntary')}
                    </Badge>
                  </td>
                  <td className="text-end">
                    {(can_write_deductions || can_archive) && (
                      <div className="flex justify-end gap-1">
                        {can_write_deductions && (
                          <Button
                            variant="secondary"
                            size="xs"
                            onClick={() => {
                              set_editing_deduction(deduction);
                              set_deduction_drawer_open(true);
                            }}
                          >
                            {t('common.edit')}
                          </Button>
                        )}
                        {can_archive && (
                          <Button
                            variant="ghost-danger"
                            size="xs"
                            onClick={() => {
                              set_archiving_id(deduction.id);
                            }}
                          >
                            {t('data_centre.archive')}
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassTable>
      )}

      {tab === 'tax' && (
        <EmptyState
          title={t('data_centre.tax_moved_title')}
          description={t('data_centre.tax_moved_description')}
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                navigate({ module: 'payroll', tab: 'policies' });
              }}
            >
              {t('data_centre.tax_moved_action')}
            </Button>
          }
        />
      )}

      {tab === 'bank' && (
        <DataCentreBankSection
          payrollSettings={payroll_settings}
          accounts={payment_source_accounts}
        />
      )}

      {tab === 'loans' && (
        <DataCentreLoanSection payrollSettings={payroll_settings} />
      )}

      <ConfirmDialog
        open={archiving_id !== null}
        onOpenChange={(open) => {
          if (!open) {
            set_archiving_id(null);
            set_archive_loading(false);
            set_archive_error(false);
          }
        }}
        title={t('data_centre.archive_confirm_title')}
        description={t('data_centre.archive_confirm_description')}
        confirmLabel={t('data_centre.archive')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={archive_loading}
        onConfirm={() => {
          if (archiving_id === null || archive_pay_item === undefined) return;
          const id = archiving_id;
          set_archive_loading(true);
          set_archive_error(false);
          void archive_pay_item(id).then((result) => {
            set_archive_loading(false);
            if (result.outcome === 'success') {
              set_archiving_id(null);
            } else {
              set_archive_error(true);
            }
          });
        }}
      >
        {archive_error && (
          <p role="alert" className="nv-help is-error">
            {t('data_centre.archive_error')}
          </p>
        )}
      </ConfirmDialog>

      <AllowanceDrawer
        open={drawer_open}
        allowance={editing}
        onClose={() => {
          set_drawer_open(false);
        }}
        onSave={
          save_allowance ??
          // Defensive fallback only -- the drawer can only be opened via the
          // add/edit buttons above, both already gated on
          // `can_write_allowances`. Keeps `onSave`'s type non-optional at the
          // call site rather than threading `| undefined` through the drawer.
          (() => Promise.resolve({ outcome: 'failure', failure: 'error' }))
        }
      />

      <DeductionDrawer
        open={deduction_drawer_open}
        deduction={editing_deduction}
        onClose={() => {
          set_deduction_drawer_open(false);
        }}
        onSave={
          save_deduction ??
          // Defensive fallback only -- mirrors `AllowanceDrawer`'s onSave
          // above: the drawer can only be opened via the add/edit buttons,
          // both already gated on `can_write_deductions`.
          (() => Promise.resolve({ outcome: 'failure', failure: 'error' }))
        }
      />
    </>
  );
}
